import { NetworkStatus } from '@apollo/client'
import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { SearchFacetInput } from '@deltek/specification-client/core/Models/searchFacetInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { sortList } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { Theme, makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import MoreVertIcon from '@material-ui/icons/MoreVert'

import {
    ActionColumnProps,
    SpecColumn,
    SpecFilters,
    SpecIdType,
    SpecSortingRule,
    SpecTable,
    TableFetchDataProps,
} from 'components/SpecTable_Old'
import { SpecTableTitleSettingsProps } from 'components/SpecTable_Old/SpecTableTitleSettings'
import { PinningProps } from 'components/SpecTable_Old/settings/TableSettings'
import useSpecTableOptions from 'components/SpecTable_Old/useSpecTableOptions'
import React, {
    Dispatch,
    MutableRefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

/**
 * Custom sort method to add the GroupBy items before the SortBy items in the array
 * when getting the data from Specpoint API.
 *
 * If the user hasn't sorted the columns first, then the default will be asc (desc: false)
 *
 * I haven't moved this utility in DSC because I don't if
 * this will still be applicable later.
 */
const getUpdatedSortBy = <TData extends Object>(
    newSortBy: SpecSortingRule<TData>[],
    newGroupBy: SpecIdType<TData>[]
) => {
    if (newGroupBy.length === 0) {
        return [newSortBy?.[newSortBy?.length - 1]]
    }

    const clonedSortBy = [...newSortBy]
    const updatedSortBy: SpecSortingRule<ListingMatch>[] = []
    newGroupBy.forEach((id) => {
        const idx = clonedSortBy.findIndex(
            (sortingRule) => sortingRule.id === id
        )
        if (idx < 0) {
            updatedSortBy.push({ id, desc: false })
        } else {
            updatedSortBy.push(clonedSortBy[idx])
            clonedSortBy.splice(idx, 1)
        }
    })

    return [...updatedSortBy, ...clonedSortBy]
}

const emptyListingMatch: Partial<ListingMatch> = {
    listingId: '',
    bimLinks: [],
    catalogs: [],
    categories: [],
    displayNameWithFirmName: '',
    firmDisplayName: '',
    firmId: '',
    firmLogoUrl: '',
    firmName: '',
    type: undefined,
    images: [],
    isActive: false,
    lastUpdated: '',
    manufacturerDocs: [],
    productId: '',
    productName: '',
    productType: '',
    sectionName: '',
    submissionStatus: '',
    videos: [],
    canAutoGenerateProductSpecification: false,
    specAgentProductTypeId: 1,
}

export const hiddenColumns: SpecIdType<ListingMatch>[] = [
    'videos',
    'images',
    'manufacturerDocs',
    'catalogs',
    'bimLinks',
]

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        position: 'relative',
        bottom: pxToRem(50),
    },
}))

interface TablePageProps {
    tableData?: Partial<ListingMatch>[]
    fetchMoreTableData: any
    tableDataNetworkStatus: NetworkStatus
    totalItems: number
    showFiltersRow: boolean
    columns: SpecColumn<ListingMatch>[]
    hiddenColumns: string[]
    pinningProps: PinningProps
    groupBy: SpecIdType<ListingMatch>[]
    fetchDataLoading: boolean
    fetchNextDataLoading: boolean
    defaultExpandedRows?: Record<string, boolean>
    autoResetExpanded?: boolean
    tableRef?: MutableRefObject<any>
    showBottomLoadingIndicator?: boolean
    setAutoResetExpanded?: (value: boolean) => void
    setFacets?: Dispatch<SetStateAction<SearchFacetInput[]>>
}

const TablePage: React.FC<TablePageProps> = ({
    tableData,
    fetchMoreTableData,
    totalItems,
    showFiltersRow,
    columns,
    hiddenColumns,
    pinningProps,
    groupBy,
    defaultExpandedRows,
    autoResetExpanded,
    tableRef,
    fetchDataLoading,
    fetchNextDataLoading,
    showBottomLoadingIndicator,
    setAutoResetExpanded,
    setFacets,
}) => {
    const classes = useStyles()
    const [sortBy, setSortBy] = useState<SpecSortingRule<ListingMatch>[]>([])

    const [filters, setFilters] = useState<SpecFilters<ListingMatch>>(
        Object.keys(emptyListingMatch).map((key) => {
            return { id: key, value: '' }
        })
    )

    // state filter from the spectable default filter controls
    const [specTableFilter, setSpecTableFilter] = useState<
        SpecFilters<ListingMatch>
    >([])

    const [rows, setRows] = useState<Partial<ListingMatch>[] | undefined>(
        tableData
    )

    const [currentMOFilter, setCurrentMOFilter] = useState<null | boolean>(null)

    const previousColLength = usePrevious(columns?.length)

    const divContainer = useRef<HTMLDivElement>()
    const [expanded, setExpanded] = useState<
        Record<SpecIdType<ListingMatch>, boolean>
    >({})

    const tableHeight = useMemo(() => {
        // we hard coded this since the only reliable source of the height is the innerheight minus 220 which is the height of the menu
        return Number(window.innerHeight - 220)
    }, [window.innerHeight])

    // this will restart a new data fetch because
    // in place.  So when those values changes, it will trigger a new data fetch.
    const fetchData = ({
        filters: updatedFilters,
        sortBy: updatedSortBy,
        groupBy: updatedGroupBy,
    }: TableFetchDataProps<ListingMatch>) => {
        setSortBy(updatedSortBy)
        // TODO: need to wire the filters on a fetch
        // filters return an array of {id, value} type
        if (!!updatedFilters && updatedFilters.length > 0) {
            setFilters(updatedFilters)
        }
    }

    const fetchNextData = () => {
        fetchMoreTableData()
    }

    // This is how you will add your own custom action component.
    // From here, you will get a "cell" prop that contains the entire row/data
    // for that row.  So, if you have the id as hidden, you can get it by calling
    // cell.row.original.
    const actionColumnProps: ActionColumnProps<ListingMatch> = {
        Cell: ({ cell }) => {
            return (
                <IconButton onClick={() => {}}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            )
        },
        width: 60,
    }

    const filterRows = useCallback(
        (
            dataToUse: ListingMatch[],
            filterToUse?: SpecFilters<ListingMatch>
        ) => {
            const moFilter = filterToUse?.find(
                (filter) => filter.id === 'hasManufacturerOptions'
            )

            if (!isEqual(moFilter?.value ?? [], currentMOFilter)) {
                setCurrentMOFilter(moFilter?.value ?? [])

                setFacets?.((past) => {
                    const tempFacets = [
                        ...past.filter(
                            (facet) => facet.name !== 'withManufacturerOption'
                        ),
                    ]

                    if (!!moFilter?.value && moFilter?.value.length) {
                        tempFacets.push({
                            name: 'withManufacturerOption',
                            values: moFilter?.value?.map((val: string) =>
                                `${val}`.toLowerCase()
                            ),
                        })
                    }

                    return tempFacets
                })
            }

            return dataToUse?.filter((row: ListingMatch) => {
                return filterToUse?.every(
                    (filter: { id: string; value: any }) => {
                        const keyName = filter?.id as keyof ListingMatch
                        if (
                            Array.isArray(row[keyName]) &&
                            keyName === 'categories'
                        ) {
                            if (
                                !(row[keyName] as string[])
                                    ?.join(', ')
                                    ?.toLowerCase()
                                    .includes(filter?.value?.toLowerCase())
                            ) {
                                return false
                            }
                        } else if (keyName === 'isActive') {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter === 'Active' &&
                                                !row[keyName]) ||
                                            (statusFilter === 'Inactive' &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (
                            keyName === 'type' ||
                            keyName === 'submissionStatus'
                        ) {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (statusFilter !== row[keyName]) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (
                            keyName === 'canAutoGenerateProductSpecification'
                        ) {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter ===
                                                'Can auto generate' &&
                                                !row[keyName]) ||
                                            (statusFilter ===
                                                "Can't auto generate" &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (keyName === 'displayNameWithFirmName') {
                            const actualString = row[keyName]
                                ?.split('%|%')
                                ?.map((val) => val?.replaceAll("'", ''))
                            const formattedString =
                                actualString?.[0] !== 'null'
                                    ? `${actualString?.[0]} (${actualString?.[1]})`
                                    : `${actualString?.[1]}`
                            return formattedString
                                ?.toLowerCase()
                                .trim()
                                .includes(filter?.value?.trim().toLowerCase())
                        } else if (keyName === 'productName') {
                            const actualString = !row[keyName]
                                ? 'No product name'
                                : row[keyName]
                            return actualString
                                ?.toLowerCase()
                                .includes(filter?.value?.trim().toLowerCase())
                        } else if (keyName === 'specAgentProductTypeId') {
                            const actualString = String(row[keyName])
                            return actualString.includes(filter?.value?.trim())
                        } else if (keyName === 'hasManufacturerOptions') {
                            return filter?.value?.some(
                                (statusFilter: string) => {
                                    if (statusFilter !== '') {
                                        if (
                                            (statusFilter === 'True' &&
                                                !row[keyName]) ||
                                            (statusFilter === 'False' &&
                                                row[keyName])
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        } else if (
                            !(row[keyName] as string)
                                ?.toLowerCase()
                                .includes(filter?.value?.trim().toLowerCase())
                        ) {
                            return false
                        }
                        return true
                    }
                )
            })
        },
        [currentMOFilter]
    )

    const prepareOrderByString = useCallback(
        (newOrderByObject: SpecSortingRule<ListingMatch>[]) => {
            const orderByString = newOrderByObject
                .map((sort) => {
                    let descString = sort.desc ? ' desc' : ''
                    if (
                        ['withManufacturerOption', 'isActive', 'canAutoGenerateProductSpecification'].includes(sort.id)
                    ) {
                        descString = sort.desc ? '' : ' desc'
                    }

                    return `${sort.id}${descString}`
                })
                .join(',')

            return orderByString
        },
        []
    )

    const orderRows = useCallback(
        (dataToUse: ListingMatch[], orderToUse: any) => {
            const sortString = `${groupBy.join(',')},${prepareOrderByString(
                orderToUse
            )}`

            return sortList([...(dataToUse ?? [])], sortString)
        },
        [groupBy]
    )

    const stateCallback = useCallback(
        (state: any) => {
            const newFilters = state?.filters?.filter(
                (filter: { key: string; value: string }) => filter.value !== ''
            )

            setSortBy(state?.sortBy)
            setFilters(newFilters)
            setExpanded(state?.expanded)

            const hasFilterChanges = !isEqual(specTableFilter, newFilters)
            const hasSortingChanges = !isEqual(sortBy, state.sortBy)

            if (hasFilterChanges) setSpecTableFilter(newFilters)
            if (
                setAutoResetExpanded &&
                (hasFilterChanges || hasSortingChanges)
            ) {
                setAutoResetExpanded(false)
            }
        },
        [tableData, specTableFilter]
    )

    useMemo(() => defaultExpandedRows, [defaultExpandedRows])

    const tableProps = useMemo(() => {
        return {
            actionColumnProps,
            allowGrouping: true,
            data: (rows as ListingMatch[]) ?? [],
            divContainerRef: divContainer,
            columns,
            expanded: !!defaultExpandedRows ? defaultExpandedRows : expanded,
            fetchData,
            fetchDataLoading,
            fetchNextData: columns.length > 0 ? fetchNextData : undefined,
            fetchNextDataLoading,
            filters,
            groupBy,
            height: tableHeight,
            hiddenColumns,
            onStateChange: stateCallback,
            pageSize: 50,
            showCheckboxColumn: true,
            specTableTitleSettings: {
                showFiltersRow: columns.length > 0 && showFiltersRow,
                showTableSettings: false,
                theme: 'light',
                hasGrouping: true,
                tableSettings: {
                    initialPinningProps: pinningProps,
                    maxGroupCount: 3,
                    showGroupOptionSetting: true,
                    showPinning: true,
                    canToggleGroup: true,
                },
                onFilterClick: () => {},
            } as SpecTableTitleSettingsProps<ListingMatch>,
            totalItems,
            noDataComponent: columns.length > 0 && <></>,
            autoResetExpanded,
            showBottomLoadingIndicator,
        }
    }, [
        rows,
        columns,
        expanded,
        filters,
        fetchData,
        fetchNextData,
        groupBy,
        stateCallback,
        showFiltersRow,
        totalItems,
        defaultExpandedRows,
        autoResetExpanded,
        fetchDataLoading,
        fetchNextDataLoading,
        showBottomLoadingIndicator,
    ])

    /**
     * useSpecTableOptions give you insight of all the exposed props that you can
     * do within the SpecTable.
     */
    const specTableProps = useSpecTableOptions<ListingMatch>(tableProps)

    useEffect(() => {
        if (columns?.length > 0) {
            let newRows = tableData as ListingMatch[]
            const nonEmptyFilters = (filters as any[])?.filter(
                (filter: { key: string; value: any }) =>
                    Array.isArray(filter?.value)
                        ? filter?.value?.length > 0
                        : filter.value !== ''
            )

            newRows = filterRows(newRows, nonEmptyFilters)

            if (sortBy?.length > 0) {
                newRows = orderRows(newRows, sortBy)
            }

            if (
                !isEqual(rows, newRows) ||
                previousColLength !== columns?.length
            ) {
                setRows(newRows)
            }
        }
    }, [
        tableData,
        filters,
        filterRows,
        sortBy,
        orderRows,
        columns,
        previousColLength,
        rows,
    ])

    return (
        <GridContainer
            className={classes.container}
            data-testid="tableContainer"
        >
            <GridItem xs={12} ref={divContainer}>
                <SpecTable
                    {...specTableProps}
                    lazyLoading={true}
                    tableMinWidth={`${pxToRem(1920)} !important`}
                    groupedCellHeight={33}
                    childRef={tableRef}
                />
            </GridItem>
        </GridContainer>
    )
}

export default TablePage
